import React, {useMemo} from "react";
import {Tooltip as MuiTooltip} from '@mui/material';

function toNumber(v) {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function isPresent(v) {
  return !(v === null || v === undefined || v === "");
}

/**
 * AmountsSummaryTooltip
 * Props:
 *  - amountsSummary: { ItemTotal, ItemTax1, ItemTax2, ItemTax, ItemTotalWithTax, TipAllocatedToItem }
 *  - data: { t: { read: (key) => string }, locale?, currency? }
 *  - labels?: optional mapping to override default labels
 *  - locale?: string (fallback to data.locale or navigator.language)
 *  - currency?: string ISO 4217 (fallback to data.currency). When provided, values are formatted as currency.
 *  - precision?: number of fraction digits when currency is not provided (default 2)
 *  - disabled?: boolean (when true, no tooltip)
 *  - placement?: MUI Tooltip placement (default 'top')
 *  - children: ReactNode to anchor the tooltip
 */
export default function AmountsSummaryTooltip({
  amountsSummary = {},
  data,
  labels,
  locale,
  currency,
  precision = 2,
  disabled = false,
  placement = 'bottom',
    onClick = () => {},
  children,
}) {
  const resolvedLocale = locale || (data && data.locale) || (typeof navigator !== 'undefined' ? navigator.language : 'en');
  const resolvedCurrency = currency || (data && data.currency);

  const nfCurrency = useMemo(() => {
    if (!resolvedCurrency) return null;
    try {
      return new Intl.NumberFormat(resolvedLocale, { style: 'currency', currency: resolvedCurrency });
    } catch {
      return null;
    }
  }, [resolvedLocale, resolvedCurrency]);

  const nfNumber = useMemo(() => {
    try {
      return new Intl.NumberFormat(resolvedLocale, { minimumFractionDigits: precision, maximumFractionDigits: Math.max(2, precision) });
    } catch {
      return { format: (n) => Number(n).toFixed(precision) };
    }
  }, [resolvedLocale, precision]);
  if (!('ItemNonRecovTax' in amountsSummary)){
      const nonRecovTax = amountsSummary?.ItemTotal - amountsSummary.ItemSubTotal - amountsSummary.TipAllocatedToItem;
      amountsSummary.ItemNonRecovTax = nonRecovTax;
  }
  
  const defaultLabels = useMemo(() => ({
      ItemSubTotal: data?.t?.read?.("amount_summary.subtotal") ?? 'Subtotal',
      ItemNonRecovTax: data?.t?.read?.("amount_summary.non_recov_tax") ?? 'Non-recov taxes',
      TipAllocatedToItem: data?.t?.read?.("amount_summary.tip_allocated_to_item") ?? 'Tip allocated',
      ItemTotal: data?.t?.read?.("amount_summary.total") ?? 'Total', 
      ItemTax1: data?.t?.read?.("amount_summary.tax1") ?? 'Tax 1', 
      ItemTax2: data?.t?.read?.("amount_summary.tax2") ?? 'Tax 2', 
      ItemTotalWithTax: data?.t?.read?.("amount_summary.total_with_tax") ?? 'Total with tax', 
  }), [data]);

  const mergedLabels = { ...defaultLabels, ...(labels || {}) };

  const orderedKeys = [
      'ItemSubTotal', 'ItemNonRecovTax', 'TipAllocatedToItem', 'ItemTotal', 'ItemTax1', 'ItemTax2' , 'ItemTotalWithTax'
  ];

  const rows = useMemo(() => {
    return orderedKeys
      .filter((k) => k in amountsSummary && isPresent(amountsSummary[k]))
      .map((k) => {
        const raw = amountsSummary[k];
        const num = toNumber(raw);
        const formatted = nfCurrency ? nfCurrency.format(num) : nfNumber.format(num);
        return { key: k, label: mergedLabels[k] || k, value: formatted };
      });
  }, [amountsSummary, mergedLabels, orderedKeys, nfCurrency, nfNumber]);

  if (disabled) {
    return <span className="inline-flex items-center align-middle">{children}</span>;
  }

  const title = (
    <div className="flex flex-col gap-1 pointer" onClick={onClick}>
        <span className="text-xs opacity-80 whitespace-nowrap mr-1">{data.t.read("amount_summary.expense_summary")}:</span>
        {rows.length === 0 ? (
        <div className="text-xs opacity-70">{data?.t?.read?.('amount_summary.empty') || 'No amounts available'}</div>
      ) : (
        rows.filter(row => row.value !== "0.00").map(({ key, label, value }) => (
          <div className="flex items-baseline justify-between gap-3" key={key}>
            <span className="text-xs opacity-80 whitespace-nowrap mr-1">{label}:</span>
            <span className="text-xs font-medium tabular-nums">${value}</span>
          </div>
        ))
      )}
        <span>
            ({data.t.read("click_to_edit")})
        </span>
    </div>
  );

  const anchor = (
    <span className="inline-flex items-center align-middle">{children}</span>
  );

  return (
    <MuiTooltip
        onClick={onClick}
      arrow
      placement={placement}
      title={title}
      componentsProps={{
        tooltip: { className: " text-lg bg-secondary" },
      }}
        slotProps={{
            tooltip: {
                sx: {
                    fontSize: '0.875rem', // equivalent to text-lg
                },
            },
        }}
    >
      {anchor}
    </MuiTooltip>
  );
}